import { CompanyLinkType } from '@api';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CompanyLinkFormValuesType } from '../../types';
import { COMPANY_LINK_FORM_SCHEMA_TRANS_PATH } from '../constants';

const buildCompanyLinkFormSchema = (t: TFunction) =>
  z.object({
    personId: z.string(),
    siteId: z.string({
      message: t(`${COMPANY_LINK_FORM_SCHEMA_TRANS_PATH}.SITE_ID_REQUIRED`),
    }),
    jobTitle: z.string(),
    contact: z.object({
      phone: z.string({
        message: t(`${COMPANY_LINK_FORM_SCHEMA_TRANS_PATH}.PHONE_REQUIRED`),
      }),
      mobile: z.string({
        message: t(`${COMPANY_LINK_FORM_SCHEMA_TRANS_PATH}.MOBILE_REQUIRED`),
      }),
      email: z
        .email({
          message: t(`${COMPANY_LINK_FORM_SCHEMA_TRANS_PATH}.EMAIL_INVALID`),
        })
        .or(z.literal('')),
      fax: z.string().nullable(),
    }),
    buyerId: z.string(),
    categories: z.array(z.string()),
    excludeFromConfirmationMails: z.boolean(),
  });

type CompanyLinkFormSchemaType = ReturnType<typeof buildCompanyLinkFormSchema>;

type UseCompanyLinkFormSchemaReturnType = {
  schema: CompanyLinkFormSchemaType;
  defaultValues: CompanyLinkFormValuesType;
};

export const useCompanyLinkFormSchema = ({
  companyLink,
}: {
  companyLink?: CompanyLinkType;
}): UseCompanyLinkFormSchemaReturnType => {
  const { t } = useTranslation();

  const schema = buildCompanyLinkFormSchema(t);

  const defaultValues: CompanyLinkFormValuesType = {
    personId: companyLink?.personId ?? '',
    siteId: companyLink?.siteId ?? '',
    jobTitle: companyLink?.jobTitle ?? '',
    contact: {
      phone: companyLink?.contact.phone ?? '',
      mobile: companyLink?.contact.mobile ?? '',
      email: companyLink?.contact.email ?? '',
      fax: companyLink?.contact.fax ?? '',
    },
    buyerId: companyLink?.buyerId ?? '',
    categories: companyLink?.categories ?? [],
    excludeFromConfirmationMails:
      companyLink?.excludeFromConfirmationMails ?? false,
  };

  return { schema, defaultValues };
};
